import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Navigate, Link, useLocation, useNavigate } from "react-router-dom";
import { PublicClientApplication } from "@azure/msal-browser";
import { ReactComponent as EyeOpen } from "../../assets/icons/eye.svg";
import { ReactComponent as EyeClosed } from "../../assets/icons/eye-slash.svg";
import { useTheme } from "../../context/ThemeContext";
import { useAuth } from "../../context/auth-context";

declare global {
  interface Window {
    google?: any;
    AppleID?: any;
  }
}

const API_BASE = "https://api.logvi-havox.com";
const DEVICE_ID_KEY = "logvi_device_id";
const REMEMBER_KEY = "eva_remember_me";

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";
const MICROSOFT_CLIENT_ID = process.env.REACT_APP_MICROSOFT_CLIENT_ID || "";
const MICROSOFT_TENANT_ID =
  process.env.REACT_APP_MICROSOFT_TENANT_ID || "common";
const APPLE_CLIENT_ID = process.env.REACT_APP_APPLE_CLIENT_ID || "";
const APPLE_REDIRECT_URI =
  process.env.REACT_APP_APPLE_REDIRECT_URI || `${window.location.origin}/login`;
const GITHUB_CLIENT_ID = process.env.REACT_APP_GITHUB_CLIENT_ID || "";
const GITHUB_REDIRECT_URI =
  process.env.REACT_APP_GITHUB_REDIRECT_URI ||
  `${window.location.origin}/login`;

function getOrCreateDeviceId(): string {
  try {
    let id = localStorage.getItem(DEVICE_ID_KEY);
    if (!id) {
      if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
        id = crypto.randomUUID();
      } else {
        id = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      }
      localStorage.setItem(DEVICE_ID_KEY, id);
    }
    return id;
  } catch {
    return "unknown-device";
  }
}

function useIsMobile(breakpoint: number = 980): boolean {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const mq = window.matchMedia(`(max-width: ${breakpoint}px)`);
    const update = () => setIsMobile(mq.matches);

    update();

    if (mq.addEventListener) {
      mq.addEventListener("change", update);
      return () => mq.removeEventListener("change", update);
    }

    // @ts-ignore
    mq.addListener(update);
    // @ts-ignore
    return () => mq.removeListener(update);
  }, [breakpoint]);

  return isMobile;
}

function useExternalScript(src: string, id: string) {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const existing = document.getElementById(id) as HTMLScriptElement | null;

    if (existing) {
      if (existing.dataset.loaded === "true") {
        setReady(true);
        return;
      }

      const onLoad = () => {
        existing.dataset.loaded = "true";
        setReady(true);
      };

      existing.addEventListener("load", onLoad);
      return () => existing.removeEventListener("load", onLoad);
    }

    const script = document.createElement("script");
    script.id = id;
    script.src = src;
    script.async = true;
    script.defer = true;

    script.onload = () => {
      script.dataset.loaded = "true";
      setReady(true);
    };

    script.onerror = () => {
      setReady(false);
    };

    document.body.appendChild(script);

    return () => {};
  }, [src, id]);

  return ready;
}

type SocialProvider = "google" | "apple" | "github" | "microsoft" | "fido2";

const GoogleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden="true">
    <path
      fill="#FFC107"
      d="M43.611 20.083H42V20H24v8h11.303C33.655 32.657 29.195 36 24 36c-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.277 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"
    />
    <path
      fill="#FF3D00"
      d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.277 4 24 4c-7.682 0-14.344 4.337-17.694 10.691z"
    />
    <path
      fill="#4CAF50"
      d="M24 44c5.093 0 9.786-1.947 13.346-5.118l-6.19-5.238C29.083 35.091 26.624 36 24 36c-5.174 0-9.623-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"
    />
    <path
      fill="#1976D2"
      d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.147 5.645l.003-.002 6.19 5.238C36.908 39.18 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"
    />
  </svg>
);

const AppleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
    <path
      fill="#111111"
      d="M16.365 1.43c0 1.14-.466 2.24-1.18 3.02-.77.84-2.03 1.49-3.08 1.41-.13-1.1.39-2.26 1.14-3.03.76-.79 2.09-1.38 3.12-1.4zM20.93 17.3c-.52 1.19-.77 1.72-1.44 2.76-.93 1.45-2.24 3.25-3.87 3.26-1.45.01-1.82-.95-3.79-.94-1.97.01-2.39.95-3.84.95-1.63.01-2.88-1.6-3.81-3.05-2.61-4.04-2.88-8.77-1.27-11.23 1.15-1.77 2.96-2.8 4.67-2.8 1.75 0 2.85.96 4.29.96 1.39 0 2.23-.96 4.28-.96 1.53 0 3.15.84 4.29 2.29-3.78 2.07-3.17 7.49.49 8.76z"
    />
  </svg>
);

const GithubIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
    <path
      fill="#111111"
      d="M12 .5C5.65.5.5 5.65.5 12.01c0 5.09 3.29 9.41 7.86 10.94.58.1.79-.25.79-.56 0-.27-.01-1.16-.02-2.1-3.2.7-3.88-1.36-3.88-1.36-.52-1.34-1.28-1.69-1.28-1.69-1.04-.72.08-.71.08-.71 1.15.08 1.76 1.18 1.76 1.18 1.02 1.76 2.68 1.25 3.34.95.1-.74.4-1.25.73-1.53-2.55-.29-5.24-1.28-5.24-5.69 0-1.26.45-2.3 1.18-3.11-.12-.29-.51-1.46.11-3.04 0 0 .97-.31 3.19 1.19a10.97 10.97 0 0 1 5.81 0c2.22-1.5 3.18-1.19 3.18-1.19.63 1.58.24 2.75.12 3.04.74.81 1.18 1.85 1.18 3.11 0 4.42-2.69 5.39-5.25 5.67.41.35.78 1.04.78 2.11 0 1.52-.01 2.74-.01 3.11 0 .31.2.67.8.56A11.52 11.52 0 0 0 23.5 12C23.5 5.65 18.35.5 12 .5z"
    />
  </svg>
);

const MicrosoftIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
    <rect x="2" y="2" width="9" height="9" fill="#F25022" />
    <rect x="13" y="2" width="9" height="9" fill="#7FBA00" />
    <rect x="2" y="13" width="9" height="9" fill="#00A4EF" />
    <rect x="13" y="13" width="9" height="9" fill="#FFB900" />
  </svg>
);

const Fido2Icon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
    <defs>
      <linearGradient id="fidoGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#00C0B7" />
        <stop offset="100%" stopColor="#1A67BF" />
      </linearGradient>
    </defs>
    <path
      d="M12 2L4 5v6c0 5.25 3.44 9.92 8 11 4.56-1.08 8-5.75 8-11V5l-8-3z"
      fill="url(#fidoGradient)"
    />
    <circle cx="12" cy="10" r="2.2" fill="#ffffff" />
    <rect x="11.2" y="12" width="1.6" height="4" rx="0.8" fill="#ffffff" />
  </svg>
);

const providerMeta: Record<
  SocialProvider,
  { label: string; icon: React.ReactNode }
> = {
  google: { label: "Entrar com Google", icon: <GoogleIcon /> },
  apple: { label: "Continuar com Apple", icon: <AppleIcon /> },
  github: { label: "Entrar com GitHub", icon: <GithubIcon /> },
  microsoft: { label: "Entrar com Microsoft", icon: <MicrosoftIcon /> },
  fido2: { label: "Entrar com FIDO2", icon: <Fido2Icon /> },
};

const SocialButton: React.FC<{
  provider: SocialProvider;
  onClick: () => void;
  disabled?: boolean;
}> = ({ provider, onClick, disabled }) => {
  const meta = providerMeta[provider];

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        width: "100%",
        minHeight: 56,
        height: 56,
        borderRadius: 14,
        border: "1px solid rgba(15, 23, 42, 0.10)",
        background: "#FFFFFF",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 12,
        cursor: disabled ? "not-allowed" : "pointer",
        transition: "all .18s ease",
        fontFamily: "Poppins, sans-serif",
        fontSize: 14,
        fontWeight: 600,
        color: "#111827",
        padding: "0 16px",
        opacity: disabled ? 0.6 : 1,
        boxSizing: "border-box",
        overflow: "hidden",
      }}
      onMouseEnter={(e) => {
        if (disabled) return;
        e.currentTarget.style.transform = "translateY(-1px)";
        e.currentTarget.style.borderColor = "rgba(14, 50, 58, 0.18)";
        e.currentTarget.style.background = "#FCFDFD";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.borderColor = "rgba(15, 23, 42, 0.10)";
        e.currentTarget.style.background = "#FFFFFF";
      }}
    >
      <span
        style={{
          width: 22,
          height: 22,
          minWidth: 22,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {meta.icon}
      </span>

      <span
        style={{
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {meta.label}
      </span>
    </button>
  );
};

const msalInstance = new PublicClientApplication({
  auth: {
    clientId: MICROSOFT_CLIENT_ID,
    authority: `https://login.microsoftonline.com/${MICROSOFT_TENANT_ID}`,
    redirectUri: `${window.location.origin}/login`,
  },
  cache: {
    cacheLocation: "sessionStorage",
    storeAuthStateInCookie: false,
  },
});

const LoginForm: React.FC = () => {
  const { refresh } = useAuth();
  const { setTheme, setSystemTitle } = useTheme();
  const isMobile = useIsMobile();
  const location = useLocation();
  const navigate = useNavigate();

  const googleReady = useExternalScript(
    "https://accounts.google.com/gsi/client",
    "google-gsi-script"
  );
  const appleReady = useExternalScript(
    "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js",
    "apple-auth-script"
  );

  const [emailOrPhone, setEmailOrPhone] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState<boolean>(() => {
    try {
      const saved = localStorage.getItem(REMEMBER_KEY);
      return saved ? JSON.parse(saved) === true : false;
    } catch {
      return false;
    }
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [socialLoading, setSocialLoading] = useState<SocialProvider | null>(
    null
  );
  const [feedback, setFeedback] = useState<{
    type: "error" | "success";
    msg: string;
  } | null>(null);

  const googleInitializedRef = useRef(false);
  const googleSdkButtonRef = useRef<HTMLDivElement | null>(null);
  const googleSdkClickableRef = useRef<HTMLElement | null>(null);

  const email = useMemo(() => emailOrPhone.trim(), [emailOrPhone]);

  useEffect(() => {
    try {
      localStorage.setItem(REMEMBER_KEY, JSON.stringify(remember));
    } catch {}
  }, [remember]);

  const applyLoginResponse = useCallback(
    async (data: any) => {
      if (data?.theme) setTheme(data.theme);
      if (data?.systemTitle) setSystemTitle(data.systemTitle);

      if (data?.accessToken) {
        try {
          localStorage.setItem("access_token", data.accessToken);
        } catch {}
      }

      await refresh();
    },
    [refresh, setTheme, setSystemTitle]
  );

  const postSocialLogin = useCallback(
    async (
      provider: "google" | "apple" | "github" | "microsoft",
      payload: Record<string, any>
    ) => {
      const deviceId = getOrCreateDeviceId();

      const res = await fetch(`${API_BASE}/auth/login`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          provider,
          remember,
          deviceId,
          ...payload,
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data?.erro || "Falha no login social.");
      }

      await applyLoginResponse(data);

      setFeedback({
        type: "success",
        msg: "Login realizado com sucesso.",
      });
    },
    [remember, applyLoginResponse]
  );

  useEffect(() => {
    if (!googleReady || !GOOGLE_CLIENT_ID) return;
    if (!window.google?.accounts?.id) return;
    if (!googleSdkButtonRef.current) return;

    if (!googleInitializedRef.current) {
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: async (response: any) => {
          if (!response?.credential) {
            setFeedback({
              type: "error",
              msg: "Google não retornou credencial válida.",
            });
            setSocialLoading(null);
            return;
          }

          try {
            setFeedback(null);
            setSocialLoading("google");
            await postSocialLogin("google", { idToken: response.credential });
          } catch (err: any) {
            setFeedback({
              type: "error",
              msg: err?.message || "Falha ao autenticar com Google.",
            });
          } finally {
            setSocialLoading(null);
          }
        },
        auto_select: false,
        cancel_on_tap_outside: true,
      });

      googleInitializedRef.current = true;
    }

    googleSdkButtonRef.current.innerHTML = "";
    googleSdkClickableRef.current = null;

    window.google.accounts.id.renderButton(googleSdkButtonRef.current, {
      theme: "outline",
      size: "large",
      type: "standard",
      shape: "rectangular",
      text: "signin_with",
      width: 240,
      logo_alignment: "left",
    });

    requestAnimationFrame(() => {
      if (!googleSdkButtonRef.current) return;

      const clickable =
        googleSdkButtonRef.current.querySelector('[role="button"]') ||
        googleSdkButtonRef.current.querySelector("div") ||
        googleSdkButtonRef.current.firstElementChild;

      googleSdkClickableRef.current = clickable as HTMLElement | null;
    });
  }, [googleReady, postSocialLogin]);

  useEffect(() => {
    if (!appleReady || !APPLE_CLIENT_ID || !window.AppleID?.auth) return;

    try {
      window.AppleID.auth.init({
        clientId: APPLE_CLIENT_ID,
        scope: "name email",
        redirectURI: APPLE_REDIRECT_URI,
        state: "apple-login",
        usePopup: true,
      });
    } catch {}
  }, [appleReady]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const provider = params.get("social_provider");
    const code = params.get("code");
    const error = params.get("error");
    const errorDescription = params.get("error_description");

    if (error) {
      setFeedback({
        type: "error",
        msg: decodeURIComponent(errorDescription || "Falha no login social."),
      });
      return;
    }

    if (provider === "github" && code) {
      (async () => {
        try {
          setSocialLoading("github");
          await postSocialLogin("github", { code });
          navigate(location.pathname, { replace: true });
        } catch (err: any) {
          setFeedback({
            type: "error",
            msg: err?.message || "Falha ao autenticar com GitHub.",
          });
        } finally {
          setSocialLoading(null);
        }
      })();
    }
  }, [location.search, location.pathname, navigate, postSocialLogin]);

  const validate = useCallback(() => {
    if (!email || !password) {
      setFeedback({
        type: "error",
        msg: "Preencha os campos para realizar o acesso.",
      });
      return false;
    }

    if (!email.includes("@")) {
      setFeedback({
        type: "error",
        msg: "Informe um e-mail válido para continuar.",
      });
      return false;
    }

    return true;
  }, [email, password]);

  const handleSubmit = useCallback(async () => {
    if (loading) return;

    setFeedback(null);

    if (!validate()) return;

    try {
      setLoading(true);

      const deviceId = getOrCreateDeviceId();

      const res = await fetch(`${API_BASE}/auth/login`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          email,
          senha: password,
          remember,
          deviceId,
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        setFeedback({
          type: "error",
          msg:
            data?.erro ||
            (res.status === 401 ? "Credenciais inválidas." : "Falha no login."),
        });
        return;
      }

      await applyLoginResponse(data);

      setFeedback({
        type: "success",
        msg: "Login realizado com sucesso.",
      });
    } catch {
      setFeedback({
        type: "error",
        msg: "Não foi possível conectar ao servidor.",
      });
    } finally {
      setLoading(false);
    }
  }, [loading, validate, email, password, remember, applyLoginResponse]);

  const handleMicrosoftLogin = useCallback(async () => {
    if (!MICROSOFT_CLIENT_ID) {
      setFeedback({
        type: "error",
        msg: "REACT_APP_MICROSOFT_CLIENT_ID não configurado.",
      });
      return;
    }

    try {
      setFeedback(null);
      setSocialLoading("microsoft");

      await msalInstance.initialize();

      const response = await msalInstance.loginPopup({
        scopes: ["openid", "profile", "email"],
        prompt: "select_account",
      });

      const idToken = response.idToken;
      if (!idToken) {
        throw new Error("Microsoft não retornou idToken.");
      }

      await postSocialLogin("microsoft", { idToken });
    } catch (err: any) {
      setFeedback({
        type: "error",
        msg: err?.message || "Falha ao autenticar com Microsoft.",
      });
    } finally {
      setSocialLoading(null);
    }
  }, [postSocialLogin]);

  const handleAppleLogin = useCallback(async () => {
    if (!APPLE_CLIENT_ID) {
      setFeedback({
        type: "error",
        msg: "REACT_APP_APPLE_CLIENT_ID não configurado.",
      });
      return;
    }

    if (!window.AppleID?.auth) {
      setFeedback({
        type: "error",
        msg: "SDK da Apple ainda não carregou.",
      });
      return;
    }

    try {
      setFeedback(null);
      setSocialLoading("apple");

      const response = await window.AppleID.auth.signIn();
      const idToken = response?.authorization?.id_token;

      if (!idToken) {
        throw new Error("Apple não retornou idToken.");
      }

      await postSocialLogin("apple", { idToken });
    } catch (err: any) {
      setFeedback({
        type: "error",
        msg: err?.message || "Falha ao autenticar com Apple.",
      });
    } finally {
      setSocialLoading(null);
    }
  }, [postSocialLogin]);

  const handleGithubLogin = useCallback(() => {
    if (!GITHUB_CLIENT_ID) {
      setFeedback({
        type: "error",
        msg: "REACT_APP_GITHUB_CLIENT_ID não configurado.",
      });
      return;
    }

    const state =
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

    sessionStorage.setItem("github_oauth_state", state);

    const redirectUri = new URL(GITHUB_REDIRECT_URI);
    redirectUri.searchParams.set("social_provider", "github");

    const githubUrl = new URL("https://github.com/login/oauth/authorize");
    githubUrl.searchParams.set("client_id", GITHUB_CLIENT_ID);
    githubUrl.searchParams.set("redirect_uri", redirectUri.toString());
    githubUrl.searchParams.set("scope", "read:user user:email");
    githubUrl.searchParams.set("state", state);

    setSocialLoading("github");
    window.location.href = githubUrl.toString();
  }, []);

  const handleGoogleLogin = useCallback(() => {
    if (loading || socialLoading) return;

    if (!GOOGLE_CLIENT_ID) {
      setFeedback({
        type: "error",
        msg: "REACT_APP_GOOGLE_CLIENT_ID não configurado.",
      });
      return;
    }

    if (!googleReady || !googleSdkClickableRef.current) {
      setFeedback({
        type: "error",
        msg: "SDK do Google ainda não está pronto.",
      });
      return;
    }

    setFeedback(null);
    googleSdkClickableRef.current.click();
  }, [loading, socialLoading, googleReady]);

  const handleSocialClick = useCallback(
    async (provider: SocialProvider) => {
      if (loading || socialLoading) return;

      if (provider === "google") return handleGoogleLogin();
      if (provider === "microsoft") return handleMicrosoftLogin();
      if (provider === "apple") return handleAppleLogin();
      if (provider === "github") return handleGithubLogin();

      setFeedback({
        type: "error",
        msg: "FIDO2 ainda não foi implementado nesta tela.",
      });
    },
    [
      loading,
      socialLoading,
      handleGoogleLogin,
      handleMicrosoftLogin,
      handleAppleLogin,
      handleGithubLogin,
    ]
  );

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !loading) {
      handleSubmit();
    }
  };

  const pageStyle: React.CSSProperties = {
    minHeight: "100vh",
    width: "100%",
    position: "relative",
    overflow: "hidden",
    background:
      "radial-gradient(circle at 8% 10%, rgba(255,156,36,0.16) 0%, rgba(255,156,36,0) 22%), radial-gradient(circle at 90% 12%, rgba(26,103,191,0.28) 0%, rgba(26,103,191,0) 24%), radial-gradient(circle at 84% 74%, rgba(0,192,183,0.17) 0%, rgba(0,192,183,0) 22%), radial-gradient(circle at 14% 78%, rgba(0,177,231,0.10) 0%, rgba(0,177,231,0) 20%), linear-gradient(135deg, #07161B 0%, #0B242B 44%, #0E323A 100%)",
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "center",
    padding: isMobile ? "26px 16px 18px" : "34px 28px 22px",
    boxSizing: "border-box",
    fontFamily: "Poppins, sans-serif",
  };

  const cardStyle: React.CSSProperties = {
    width: "100%",
    maxWidth: 540,
    borderRadius: 28,
    background: "rgba(255,255,255,0.96)",
    border: "1px solid rgba(255,255,255,0.18)",
    padding: isMobile ? "24px 18px 22px" : "34px 34px 28px",
    boxSizing: "border-box",
    zIndex: 2,
    marginTop: isMobile ? "26px" : "50px",
    backdropFilter: "blur(10px)",
  };

  const topBrandStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 14,
    marginBottom: 18,
  };

  const formTitleStyle: React.CSSProperties = {
    margin: "10px 0 8px",
    color: "#0F172A",
    fontSize: isMobile ? 30 : 42,
    lineHeight: 1.04,
    fontWeight: 700,
    letterSpacing: "-0.03em",
  };

  const labelStyle: React.CSSProperties = {
    display: "block",
    fontSize: 13,
    fontWeight: 600,
    color: "#334155",
    marginBottom: 8,
  };

  const fieldGroupStyle: React.CSSProperties = {
    width: "100%",
    marginBottom: 16,
  };

  const inputWrapStyleBase: React.CSSProperties = {
    width: "100%",
    minHeight: 58,
    borderRadius: 16,
    border: "1px solid #D7DEE6",
    background: "#F8FAFC",
    display: "flex",
    alignItems: "center",
    padding: "0 16px",
    boxSizing: "border-box",
    position: "relative",
    transition: "all .18s ease",
  };

  const inputStyle: React.CSSProperties = {
    width: "100%",
    height: "100%",
    border: "none",
    outline: "none",
    background: "transparent",
    color: "#0F172A",
    fontSize: 15,
    fontWeight: 500,
    fontFamily: "Poppins, sans-serif",
    paddingRight: 42,
  };

  const eyeButtonStyle: React.CSSProperties = {
    position: "absolute",
    right: 14,
    top: "50%",
    transform: "translateY(-50%)",
    width: 34,
    height: 34,
    borderRadius: 10,
    border: "none",
    background: "transparent",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    color: "#64748B",
  };

  const rememberRowStyle: React.CSSProperties = {
    width: "100%",
    display: "flex",
    alignItems: isMobile ? "flex-start" : "center",
    justifyContent: "space-between",
    gap: 12,
    marginTop: 4,
    marginBottom: 20,
    flexDirection: isMobile ? "column" : "row",
  };

  const checkboxLabelStyle: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: 10,
    cursor: "pointer",
    userSelect: "none",
  };

  const checkboxInputStyle: React.CSSProperties = {
    position: "absolute",
    opacity: 0,
    width: 0,
    height: 0,
    pointerEvents: "none",
  };

  const checkboxBoxStyle: React.CSSProperties = {
    width: 22,
    height: 22,
    borderRadius: 30,
    border: "1px solid #CAD5E2",
    background: remember ? "#0E323A" : "#FFFFFF",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all .18s ease",
  };

  const checkIcon = (
    <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M20 6L9 17l-5-5"
        fill="none"
        stroke="#ffffff"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  const linkStyle: React.CSSProperties = {
    color: "#0E323A",
    fontSize: 13,
    fontWeight: 600,
    textDecoration: "none",
  };

  const primaryButtonStyle: React.CSSProperties = {
    width: "100%",
    minHeight: 58,
    borderRadius: 16,
    border: "none",
    cursor: loading ? "not-allowed" : "pointer",
    background: "#0E323A",
    color: "#FFFFFF",
    fontFamily: "Poppins, sans-serif",
    fontSize: 16,
    fontWeight: 700,
    letterSpacing: 0.2,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all .18s ease",
    marginBottom: 18,
    opacity: loading ? 0.78 : 1,
  };

  const dividerWrapStyle: React.CSSProperties = {
    width: "100%",
    display: "flex",
    alignItems: "center",
    gap: 12,
    margin: "10px 0 18px",
  };

  const dividerLineStyle: React.CSSProperties = {
    flex: 1,
    height: 1,
    background: "rgba(148, 163, 184, 0.28)",
  };

  const dividerTextStyle: React.CSSProperties = {
    fontSize: 12,
    fontWeight: 600,
    color: "#94A3B8",
    letterSpacing: 0.4,
    textTransform: "uppercase",
    whiteSpace: "nowrap",
  };

  const socialGridStyle: React.CSSProperties = {
    display: "grid",
    gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
    gap: 12,
    width: "100%",
    alignItems: "stretch",
  };

  const bottomRowStyle: React.CSSProperties = {
    marginTop: 22,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    flexWrap: "wrap",
  };

  const mutedTextStyle: React.CSSProperties = {
    color: "#64748B",
    fontSize: 13,
    lineHeight: 1.6,
  };

  const feedbackStyle = (type: "error" | "success"): React.CSSProperties => ({
    width: "100%",
    marginBottom: 16,
    borderRadius: 14,
    padding: "13px 14px",
    fontSize: 13,
    fontWeight: 600,
    lineHeight: 1.5,
    border:
      type === "error"
        ? "1px solid rgba(220, 38, 38, 0.14)"
        : "1px solid rgba(22, 163, 74, 0.14)",
    background:
      type === "error" ? "rgba(254, 242, 242, 1)" : "rgba(240, 253, 244, 1)",
    color: type === "error" ? "#B91C1C" : "#15803D",
  });

  return (
    <div style={pageStyle}>
      <div
        style={{
          position: "absolute",
          inset: 0,
          overflow: "hidden",
          pointerEvents: "none",
        }}
      >
        <div
          style={{
            position: "absolute",
            width: 300,
            height: 300,
            borderRadius: 42,
            left: -70,
            top: 24,
            transform: "rotate(28deg)",
            background: "rgba(255, 156, 36, 0.50)",
            border: "1px solid rgba(255, 156, 36, 0.14)",
          }}
        />
        <div
          style={{
            position: "absolute",
            width: 220,
            height: 220,
            borderRadius: 34,
            left: "11%",
            top: "18%",
            transform: "rotate(45deg)",
            background: "rgba(26, 103, 191, 0.50)",
            border: "1px solid rgba(26, 103, 191, 0.14)",
          }}
        />
        <div
          style={{
            position: "absolute",
            width: 290,
            height: 290,
            borderRadius: 42,
            right: "8%",
            top: 56,
            transform: "rotate(18deg)",
            background: "rgba(0, 192, 183, 0.44)",
            border: "1px solid rgba(0, 192, 183, 0.14)",
          }}
        />
        <div
          style={{
            position: "absolute",
            width: 160,
            height: 160,
            borderRadius: 22,
            right: "19%",
            top: "31%",
            transform: "rotate(-22deg)",
            background: "rgba(0, 177, 231, 0.65)",
            border: "1px solid rgba(0, 177, 231, 0.14)",
          }}
        />
        <div
          style={{
            position: "absolute",
            width: 420,
            height: 420,
            borderRadius: "50%",
            right: -120,
            bottom: -120,
            background: "rgba(26, 103, 191, 0.30)",
          }}
        />
        <div
          style={{
            position: "absolute",
            width: 240,
            height: 240,
            borderRadius: "50%",
            left: -80,
            bottom: "10%",
            background: "rgba(0, 192, 183, 0.38)",
            filter: "blur(1px)",
          }}
        />
      </div>

      <div style={cardStyle}>
        <div style={topBrandStyle}></div>

        <div style={formTitleStyle}>Entrar no Logvi</div>

        {feedback && (
          <div style={feedbackStyle(feedback.type)} role="alert">
            {feedback.msg}
          </div>
        )}

        <div style={fieldGroupStyle}>
          <label style={labelStyle}>E-mail corporativo</label>
          <div
            style={inputWrapStyleBase}
            onFocusCapture={(e) => {
              (e.currentTarget as HTMLDivElement).style.borderColor = "#0E323A";
              (e.currentTarget as HTMLDivElement).style.background = "#FFFFFF";
            }}
            onBlurCapture={(e) => {
              (e.currentTarget as HTMLDivElement).style.borderColor = "#D7DEE6";
              (e.currentTarget as HTMLDivElement).style.background = "#F8FAFC";
            }}
          >
            <input
              type="text"
              value={emailOrPhone}
              onChange={(e) => setEmailOrPhone(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="seuemail@empresa.com"
              autoComplete="username"
              style={inputStyle}
            />
          </div>
        </div>

        <div style={fieldGroupStyle}>
          <label style={labelStyle}>Senha</label>
          <div
            style={inputWrapStyleBase}
            onFocusCapture={(e) => {
              (e.currentTarget as HTMLDivElement).style.borderColor = "#0E323A";
              (e.currentTarget as HTMLDivElement).style.background = "#FFFFFF";
            }}
            onBlurCapture={(e) => {
              (e.currentTarget as HTMLDivElement).style.borderColor = "#D7DEE6";
              (e.currentTarget as HTMLDivElement).style.background = "#F8FAFC";
            }}
          >
            <input
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Digite sua senha"
              autoComplete="current-password"
              style={inputStyle}
            />

            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
              style={eyeButtonStyle}
            >
              {showPassword ? <EyeClosed /> : <EyeOpen />}
            </button>
          </div>
        </div>

        <div style={rememberRowStyle}>
          <label htmlFor="rememberMe" style={checkboxLabelStyle}>
            <input
              id="rememberMe"
              type="checkbox"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
              style={checkboxInputStyle}
            />

            <span aria-hidden="true" style={checkboxBoxStyle}>
              {remember ? checkIcon : null}
            </span>

            <span
              style={{
                color: "#525A66",
                fontSize: 13,
                fontWeight: 500,
              }}
            >
              Manter sessão ativa
            </span>
          </label>

          <Link to="/redefinir-senha" style={linkStyle}>
            Esqueceu sua senha?
          </Link>
        </div>

        <button
          type="button"
          onClick={handleSubmit}
          disabled={loading}
          style={primaryButtonStyle}
          onMouseEnter={(e) => {
            if (!loading) e.currentTarget.style.transform = "translateY(-1px)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "translateY(0)";
          }}
        >
          {loading ? "Entrando..." : "Continuar"}
        </button>

        <div style={dividerWrapStyle}>
          <div style={dividerLineStyle} />
          <div style={dividerTextStyle}>ou</div>
          <div style={dividerLineStyle} />
        </div>

        <div style={socialGridStyle}>
          <SocialButton
            provider="google"
            disabled={loading || !!socialLoading || !googleReady}
            onClick={() => handleSocialClick("google")}
          />

          <SocialButton
            provider="apple"
            disabled={loading || !!socialLoading}
            onClick={() => handleSocialClick("apple")}
          />

          <SocialButton
            provider="github"
            disabled={loading || !!socialLoading}
            onClick={() => handleSocialClick("github")}
          />

          <SocialButton
            provider="microsoft"
            disabled={loading || !!socialLoading}
            onClick={() => handleSocialClick("microsoft")}
          />
        </div>

        <div style={dividerWrapStyle}>
          <div style={dividerLineStyle} />
          <span style={dividerTextStyle}>Acesso seguro</span>
          <div style={dividerLineStyle} />
        </div>

        <SocialButton
          provider="fido2"
          disabled={loading || !!socialLoading}
          onClick={() => handleSocialClick("fido2")}
        />

        <div style={bottomRowStyle}>
          <div style={mutedTextStyle}>
            Primeiro acesso?{" "}
            <Link
              to="null"
              style={{
                color: "#0E323A",
                fontWeight: 700,
                textDecoration: "none",
              }}
            >
              Solicitar cadastro
            </Link>
          </div>
        </div>

        <div
          aria-hidden="true"
          style={{
            position: "fixed",
            left: -9999,
            top: -9999,
            width: 1,
            height: 1,
            opacity: 0,
            pointerEvents: "none",
            overflow: "hidden",
          }}
        >
          <div ref={googleSdkButtonRef} />
        </div>
      </div>
    </div>
  );
};

const AcessLogin: React.FC = () => {
  const { sessao, loading } = useAuth();

  if (loading) return null;

  if (sessao) {
    const isSolicitante = Boolean(sessao.roles?.includes("solicitante"));

    return (
      <Navigate
        to={
          isSolicitante
            ? "/minhas-solicitacoes/laboratorio-cases"
            : "/suprimentos/produtos"
        }
        replace
      />
    );
  }

  return <LoginForm />;
};

export default AcessLogin;
